// Words for the codes the API returns. A person reads these, never the raw enum or an id.

/** snake_case, kebab-case, dotted or SHOUTING codes → "Plain words". */
export function humanize(v: unknown): string {
  if (v === null || v === undefined || v === '') return '—';
  const s = String(v).replace(/[_.\-:]+/g, ' ').trim().toLowerCase()
    // acronyms keep their capitals: "Ai spend" and "Icp status" read as AI and ICP
    .replace(/\b(ai|api|crm|csv|dsar|icp|id|mcp|mrr|seo|sms|url|usd)\b/g, (m) => m.toUpperCase());
  return s ? s[0]!.toUpperCase() + s.slice(1) : '—';
}

/** Looks the code up in a map, else humanizes it. */
export const say = (map: Record<string, string>, v: unknown): string => (v === null || v === undefined || v === '' ? '—' : map[String(v)] ?? humanize(v));

const UUID = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i;
export const isId = (v: unknown) => typeof v === 'string' && UUID.test(v);

/** A time a person reads: "just now", "5 min ago", "3 h ago", "yesterday", then "Oct 3" (and the year when it is not this one). */
export function when(v: unknown, now = Date.now()): string {
  if (v === null || v === undefined || v === '') return '—';
  const d = new Date(v as string);
  if (Number.isNaN(d.getTime())) return String(v);
  const diff = now - d.getTime();
  const min = Math.round(diff / 60_000);
  if (diff >= 0) {
    if (min < 1) return 'just now';
    if (min < 60) return `${min} min ago`;
    if (min < 24 * 60) return `${Math.round(min / 60)} h ago`;
    if (min < 48 * 60) return 'yesterday';
    if (min < 7 * 24 * 60) return `${Math.floor(min / (24 * 60))} days ago`;
  } else if (-min < 24 * 60 && -min >= 1) return `in ${-min < 60 ? `${-min} min` : `${Math.round(-min / 60)} h`}`;
  const same = d.getFullYear() === new Date(now).getFullYear();
  return d.toLocaleDateString('en-US', { month: 'short', day: 'numeric', ...(same ? {} : { year: 'numeric' }) });
}

export const ROLE: Record<string, string> = {
  owner: 'Owner', admin: 'Admin', sales_approver: 'Sales approver', marketing_approver: 'Marketing approver', rep: 'Sales rep', viewer: 'Viewer', developer: 'Developer',
};
export const ROLE_HELP: Record<string, string> = {
  owner: 'Everything, including billing, plan changes and the spend cap.',
  admin: 'Manages members, integrations, kill switches and settings.',
  sales_approver: 'Signs the ICP, the outbound policy and the sales-ready criteria.',
  marketing_approver: 'Signs the ICP, audience policies and campaign emails.',
  rep: 'Receives handoffs and works the leads assigned to them.',
  viewer: 'Reads everything, changes nothing.',
  developer: 'Creates API keys and webhooks.',
};
export const ROLES = Object.keys(ROLE);
/** "a sales approver", "an owner": for sentences. */
export const aRole = (r: string) => { const n = say(ROLE, r).toLowerCase(); return `${/^[aeiou]/.test(n) ? 'an' : 'a'} ${n}`; };
/** Roles arrive as an array or as Postgres' "{owner,rep}". */
export const roleList = (v: unknown): string[] => (Array.isArray(v) ? v.map(String) : String(v ?? '').replace(/[{}"]/g, '').split(/[\s,]+/).filter(Boolean));
export const roles = (v: unknown) => roleList(v).map((r) => say(ROLE, r)).join(', ') || '—';

/** Policy types: what the approver is signing. */
export const KIND: Record<string, string> = {
  icp_version: 'Ideal customer profile', outbound_policy: 'Outbound policy', first_campaign_messages: 'First campaign’s emails', audience_policy: 'Audience policy',
  sales_ready_criteria: 'Sales-ready criteria', integration_mapping: 'Field mapping', spend_cap: 'Spend cap',
};

export const ACTION_TYPE: Record<string, string> = {
  crm_task: 'CRM task', outbound_step: 'Outbound email', ad_audience: 'Ad audience push', research_request: 'Research request', handoff: 'Handoff to sales',
};
export const RISK: Record<string, string> = {
  internal: 'Inside Outlevo', own_systems: 'Your own systems', third_party_contact: 'Contacts people', spend: 'Spends money',
};

export const STATUS: Record<string, string> = {
  eligible_for_action: 'Ready for outreach', research_required: 'Needs research', unresearched: 'Not researched', not_connected: 'Not connected',
  token_error: 'Sign-in expired', past_due: 'Payment overdue', in_progress: 'In progress', proposed: 'Proposed', executing: 'Running', executed: 'Done',
  exhausted: 'Used up', revoked: 'Withdrawn', superseded: 'Replaced',
};

export const ACCOUNT_CLASS: Record<string, string> = {
  confirmed_icp: 'Confirmed fit', probable_fit: 'Probable fit', experimental_segment: 'Experimental segment', disqualified: 'Disqualified', research_required: 'Needs research',
};

/** Why an account has its status. */
const REASON: Record<string, string> = {
  hard_constraints_pass: 'Meets every hard constraint', no_fit_criteria: 'No fit criteria set, so constraints decide', country: 'Country is outside your markets',
  size: 'Company size is outside your range', business_model: 'Business model does not match', industry: 'Industry is excluded', existing_customer: 'Already a customer',
  open_opportunity: 'Has an open opportunity',
  site_unreadable: 'The website could not be read: correct its address or add a few facts, then research again',
};
export function reason(r: unknown): string {
  const s = String(r ?? '');
  const [code, arg] = s.split(':');
  // the criterion the check could not confirm, in the model's words (research/jobs.ts)
  if (code === 'why') return `Why: ${s.slice(4)}`;
  if (code === 'missing') return `Unknown ${humanize(arg).toLowerCase()}: more research needed`;
  if (code === 'icp_fit') return `ICP fit score ${arg}`;
  if (code === 'fit_unconfirmed') return 'Fit not confirmed yet: the pages read did not show it meets your segment';
  return REASON[s] ?? (typeof r === 'object' ? humanize((r as { code?: string }).code ?? '') : humanize(s));
}

export const OPERATION: Record<string, string> = {
  read: 'API reads', state_change: 'Changes through the API', webhook_delivered: 'Webhook deliveries', email_verification: 'Email checks', qualification: 'Account research and qualification',
  contact_discovery: 'Finding contacts', deep_research: 'Deep research', ads_match_enrich: 'Improve matches (personal email and phone found)',
};
export const PLAN: Record<string, string> = { free: 'Free', builder: 'Builder', growth: 'Growth', managed: 'Managed', none: 'No plan' };

export const SOURCE_TYPE: Record<string, string> = { evidence: 'Uploaded evidence', user: 'A person', proposal: 'An accepted proposal', crm: 'Your CRM', system: 'Outlevo' };
export const AUTHORITY: Record<string, string> = {
  customer_policy: 'Company policy (binding)', verified_observation: 'Verified from a source', human_confirmed: 'Confirmed by a person', ai_inference: 'Inferred by AI',
  experimental_hypothesis: 'Hypothesis to test', unknown: 'Unknown',
};
export const TOPIC: Record<string, string> = { profile: 'Company profile', positioning: 'Positioning', customer: 'Customers', case_study: 'Case study', product: 'Product', metric: 'Metric' };
export const INITIATOR: Record<string, string> = { user: 'A person', api_key: 'An API key', system: 'Outlevo', operator: 'Outlevo staff', ai: 'The assistant' };
export const OUTCOME: Record<string, string> = {
  reply: 'Reply', sales_accepted: 'Accepted by sales', meeting_held: 'Meeting held', opportunity_created: 'Opportunity created', closed_won: 'Deal won', closed_lost: 'Deal lost',
};
export const LINK_TYPE: Record<string, string> = { direct: 'Direct', assisted: 'Assisted' };
export const COMPETITOR_KIND: Record<string, string> = { direct: 'Direct competitor', adjacent: 'Adjacent product', status_quo: 'Doing nothing / in-house' };
export const SCOPE: Record<string, string> = { org: 'Whole workspace', campaign: 'Campaign', mailbox: 'Mailbox', audience: 'Audience', assessment: 'Assessments (platform)', content: 'Content (platform)' };

/** Why a Revenue Action is blocked, from the API's error code. */
export const BLOCKED: Record<string, string> = {
  KILL_SWITCH_ACTIVE: 'A kill switch is on', CONTACT_NOT_ELIGIBLE: 'The contact may not be contacted', SUPPRESSION_ACTIVE: 'The contact is on the suppression list',
  JURISDICTION_NOT_REVIEWED: 'This country is not reviewed yet', CAPABILITY_UNAVAILABLE: 'This channel is not available yet', PROVIDER_ACCESS_REQUIRED: 'The provider has not granted access yet',
  ICP_APPROVAL_REQUIRED: 'The ICP needs approval', CAMPAIGN_APPROVAL_REQUIRED: 'The campaign needs approval', FIRST_BATCH_REVIEW: 'Waits for you to review the campaign\'s first emails', POLICY_APPROVAL_REQUIRED: 'Needs an approved policy',
  POLICY_BOUND_EXCEEDED: 'Over a limit in the approved policy', QUOTA_EXCEEDED: 'Out of credits', ACCOUNT_NOT_QUALIFIED: 'The account is not qualified',
  SENDER_NOT_READY: 'The sender is not ready', HARD_CONSTRAINT_VIOLATION: 'Breaks a hard constraint', INSUFFICIENT_EVIDENCE: 'Not enough evidence',
  CONNECTION_DISCONNECTED: 'The integration is disconnected', HOLDOUT_ACCOUNT: 'The account is in the holdout group', ORG_INACTIVE: 'The workspace is inactive', WORKSPACE_CLOSED: 'The workspace was closed',
  AUDIENCE_TOO_SMALL: 'The audience is too small', ADS_ACCOUNT_NOT_ELIGIBLE: 'The ad account is not eligible',
};

/** Column key → how its value reads. Table and KV use this when a column has no render of its own. */
const BY_KEY: Record<string, Record<string, string>> = {
  action_type: ACTION_TYPE, risk_class: RISK, account_class: ACCOUNT_CLASS, operation: OPERATION, plan: PLAN, source_type: SOURCE_TYPE, authority: AUTHORITY,
  topic: TOPIC, initiator_type: INITIATOR, actor_type: INITIATOR, created_by_type: INITIATOR, link_type: LINK_TYPE, blocked_code: BLOCKED, scope_type: SCOPE,
};
export function cell(key: string, v: unknown): string | null {
  if (key === 'roles') return roles(v);
  if (key === 'type' && typeof v === 'string' && (OUTCOME[v] || KIND[v])) return OUTCOME[v] ?? KIND[v]!;
  const map = BY_KEY[key];
  if (map) return say(map, v);
  if (isId(v)) return '—';
  if (/_at$/.test(key) && typeof v === 'string') return when(v);
  // money columns ("ai_spend_30d", "cost_usd") as dollars; cents of a cent stay visible for AI spend
  if (/(^|_)(spend|cost)(_|$)|(^|_)usd$/.test(key) && v !== null && v !== '' && Number.isFinite(Number(v))) {
    const n = Number(v);
    return `$${n.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: n && Math.abs(n) < 1 ? 4 : 2 })}`;
  }
  return null;
}

const HEAD: Record<string, string> = { status_at: 'Status changed', n: 'Count', spf_ok: 'SPF', dkim_ok: 'DKIM', dmarc_ok: 'DMARC', usd: 'Cost', id: '' };
/** A column or field name as a heading: "created_at" → "Created", "account_class" → "Account class". */
export const heading = (k: string) => HEAD[k] ?? humanize(k.replace(/_at$/, ''));
